import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Button,
  Card,
  Input,
  Textarea,
  PageHeader,
  PageLayout,
  FormField,
  Toolbar,
  Table,
  THead,
  TBody,
  TRow,
  TH,
  TD,
  Modal,
  useModal,
  Pagination,
  useToast,
} from "@dawa/ui";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";

type Sample = {
  id: string;
  title: string | null;
  content_text: string;
  created_at: string;
  lang?: string | null;
};
type Project = {
  id: string;
  name: string;
  domain?: string | null;
  sitemap_url?: string | null;
  gsc_property?: string | null;
};
type PageRow = { url: string; slug: string; title?: string | null };

export default function ProjectDetail() {
  const { id } = useParams();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [lang, setLang] = useState<string>("");
  const [htmlMode, setHtmlMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [voiceTokens, setVoiceTokens] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const { Modal: AddModal, openModal, closeModal } = useModal();
  const { Modal: EditModal, openModal: openEditModal, closeModal: closeEditModal } = useModal();
  const [editing, setEditing] = useState<Sample | null>(null);
  const { addToast, Toasts } = useToast();
  const [project, setProject] = useState<Project | null>(null);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [pagesQ, setPagesQ] = useState("");
  const [pagesPage, setPagesPage] = useState(1);
  const pagesPageSize = 10;
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  const sanitizedText = htmlMode
    ? text
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/on[a-z]+\s*=\s*"[^"]*"/gi, "")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : text.trim();
  const sanitizedLength = sanitizedText.length;
  const lengthOk = sanitizedLength >= 500 && sanitizedLength <= 50000;
  const urlIsValid = (() => {
    if (!url.trim()) return false;
    try {
      const parsed = new URL(url.trim());
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  })();

  async function load() {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token || !id) return;
    // Project details
    const pr = await apiFetch(`/v1/projects/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const pj = await pr.json().catch(() => null);
    if (pj && typeof pj === "object") setProject(pj as Project);
    // Samples
    const res = await apiFetch(`/v1/projects/${id}/samples`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const arr = await res.json().catch(() => []);
    setSamples(Array.isArray(arr) ? arr : []);
    // Pages
    const prg = await apiFetch(`/v1/projects/${id}/pages`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const pgArr = await prg.json().catch(() => []);
    setPages(Array.isArray(pgArr) ? (pgArr as PageRow[]) : []);
    const vp = await apiFetch(`/v1/projects/${id}/voice-profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const vj = await vp.json().catch(() => ({}));
    setVoiceTokens(typeof vj.tokens === "number" ? vj.tokens : null);
  }

  useEffect(() => {
    void load();
  }, [id]);

  function detectLang(t: string): string {
    const fr = (t.match(/\b(le|la|les|des|vous|nous|est|être|avec|sans)\b/gi) || []).length;
    const en = (t.match(/\b(the|and|you|we|is|are|with|without)\b/gi) || []).length;
    if (fr > en) return "fr";
    if (en > fr) return "en";
    return "";
  }

  async function addSampleFromText() {
    if (!lengthOk) {
      setAddError("Le texte doit contenir entre 500 et 50 000 caractères.");
      return false;
    }
    setLoading(true);
    setAddError(null);
    try {
      const s = await supabase.auth.getSession();
      const token = s.data.session?.access_token;
      const res = await apiFetch(`/v1/projects/${id}/samples`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title,
          content_text: sanitizedText,
          lang: lang || detectLang(sanitizedText),
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.ok) {
        addToast("Référence ajoutée", "success");
        setTitle("");
        setText("");
        setLang("");
        await load();
        return true;
      } else {
        const message = typeof payload.error === "string" ? payload.error : "Échec de l'ajout";
        addToast(message, "error");
        setAddError(message);
        return false;
      }
    } finally {
      setLoading(false);
    }
  }

  async function importFromUrl() {
    if (!urlIsValid) {
      setImportError("URL invalide. Utilisez une URL http(s) complète.");
      return;
    }
    setLoading(true);
    setImportError(null);
    setImportSuccess(null);
    try {
      const s = await supabase.auth.getSession();
      const token = s.data.session?.access_token;
      const res = await apiFetch(`/v1/projects/${id}/samples/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url, title, lang: lang || null }),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.ok) {
        const size = typeof payload.size === "number" ? payload.size : null;
        const message = size ? `Référence importée (${size} caractères)` : "Référence importée";
        addToast(message, "success");
        setImportSuccess(message);
        setUrl("");
        setTitle("");
        await load();
      } else {
        let message = "Échec de l'import";
        if (typeof payload.error === "string") {
          if (payload.error === "empty_content") {
            message = "Le contenu n'a pas pu être récupéré depuis cette URL.";
          } else if (payload.error.startsWith("fetch_failed_")) {
            message = "Impossible de récupérer le contenu depuis cette URL.";
          } else {
            message = payload.error;
          }
        }
        addToast(message, "error");
        setImportError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function importSitemap() {
    if (!id || !project?.sitemap_url) return;
    setImporting(true);
    try {
      const s = await supabase.auth.getSession();
      const token = s.data.session?.access_token;
      const res = await apiFetch(`/v1/projects/${id}/sitemap/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        addToast("Sitemap importé", "success");
        await load();
      } else {
        addToast("Échec de l'import du sitemap", "error");
      }
    } finally {
      setImporting(false);
    }
  }

  async function updateSample(smp: Sample) {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    const res = await apiFetch(`/v1/projects/${id}/samples/${smp.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ title: smp.title, content_text: smp.content_text }),
    });
    addToast(
      res.ok ? "Référence mise à jour" : "Échec de la mise à jour",
      res.ok ? "success" : "error",
    );
    await load();
  }

  async function removeSample(sid: string) {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    const res = await apiFetch(`/v1/projects/${id}/samples/${sid}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    addToast(
      res.ok ? "Référence supprimée" : "Échec de la suppression",
      res.ok ? "success" : "error",
    );
    await load();
  }

  const filtered = q
    ? samples.filter((s) => (s.title || "").toLowerCase().includes(q.toLowerCase()))
    : samples;
  const total = filtered.length;
  const slice = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <PageLayout maxWidth="full" className="space-y-6">
      <PageHeader
        title="Projet"
        breadcrumbs={[{ label: "Projets", href: "/projects" }, { label: "Détail" }]}
        actions={
          <Link to="/projects">
            <Button variant="secondary">Retour</Button>
          </Link>
        }
      />
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <div className="text-base font-semibold">{project?.name || "Projet"}</div>
            <div className="text-sm text-muted">{project?.domain || "—"}</div>
            <div className="text-xs text-muted">Sitemap: {project?.sitemap_url || "—"}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted">{pages.length} pages importées</div>
            <Button
              onClick={() => void importSitemap()}
              disabled={!project?.sitemap_url || importing}
            >
              {importing ? "Import..." : "Importer sitemap"}
            </Button>
          </div>
        </div>
      </Card>
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold">Articles de référence</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-warning">
              Plus vous ajoutez de texte, plus le coût en tokens peut augmenter.
            </div>
            {voiceTokens !== null && (
              <div className="text-xs text-muted">Profil ~{voiceTokens} tokens</div>
            )}
            <Button
              onClick={() => {
                setAddError(null);
                setImportError(null);
                setImportSuccess(null);
                openModal();
              }}
            >
              Ajouter
            </Button>
          </div>
        </div>
        <AddModal>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Ajouter une référence</h3>
            <FormField label="Titre (optionnel)" htmlFor="modal-title">
              <Input id="modal-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </FormField>
            <FormField
              label="Importer par URL"
              htmlFor="modal-url"
              help="Extraction HTML→texte naïve"
            >
              <div className="flex gap-2">
                <Input
                  id="modal-url"
                  className="flex-1"
                  placeholder="https://..."
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setImportSuccess(null);
                  }}
                />
                <Button
                  type="button"
                  variant="subtle"
                  onClick={() => void importFromUrl()}
                  disabled={loading || !urlIsValid}
                >
                  {importSuccess ? "Importé" : "Importer"}
                </Button>
              </div>
              {importSuccess && (
                <div className="mt-2 flex items-center justify-between rounded-sm bg-success/10 px-3 py-2 text-xs text-success">
                  <span>{importSuccess}</span>
                  <Button type="button" size="sm" variant="secondary" onClick={closeModal}>
                    Fermer
                  </Button>
                </div>
              )}
              {importError && <div className="mt-1 text-xs text-warning">{importError}</div>}
            </FormField>
            <FormField label="Langue" htmlFor="modal-lang">
              <select
                id="modal-lang"
                className="w-full rounded-sm border border-muted bg-surface px-3 py-2"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
              >
                <option value="">Auto</option>
                <option value="fr">Français</option>
                <option value="en">Anglais</option>
                <option value="es">Espagnol</option>
                <option value="de">Allemand</option>
              </select>
            </FormField>
            <div className="flex items-center gap-2">
              <input
                id="htmlmode"
                type="checkbox"
                checked={htmlMode}
                onChange={(e) => setHtmlMode(e.target.checked)}
              />
              <label htmlFor="htmlmode" className="text-sm">
                Collage HTML (sanitization avant envoi)
              </label>
            </div>
            <FormField label="Texte (min 500 / max 50000)" htmlFor="modal-text">
              <div>
                <Textarea
                  id="modal-text"
                  rows={8}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <div className={`mt-1 text-xs ${lengthOk ? "text-muted" : "text-warning"}`}>
                  {sanitizedLength} caractères {htmlMode ? "(après nettoyage)" : ""}
                </div>
                {addError && <div className="mt-1 text-xs text-warning">{addError}</div>}
              </div>
            </FormField>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeModal}>
                Annuler
              </Button>
              <Button
                onClick={async () => {
                  const ok = await addSampleFromText();
                  if (ok) closeModal();
                }}
                disabled={loading || !lengthOk}
              >
                Ajouter
              </Button>
            </div>
          </div>
        </AddModal>
      </Card>
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-base font-semibold">Pages importées</div>
          <div className="text-xs text-muted">{pages.length} au total</div>
        </div>
        <Toolbar
          searchValue={pagesQ}
          onSearchChange={(v) => {
            setPagesQ(v);
            setPagesPage(1);
          }}
          placeholder="Rechercher par URL ou titre"
        />
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TRow>
                <TH>URL</TH>
                <TH>Titre</TH>
              </TRow>
            </THead>
            <TBody>
              {(pagesQ
                ? pages.filter(
                    (p) =>
                      p.url.toLowerCase().includes(pagesQ.toLowerCase()) ||
                      (p.title || "").toLowerCase().includes(pagesQ.toLowerCase()),
                  )
                : pages
              )
                .slice((pagesPage - 1) * pagesPageSize, pagesPage * pagesPageSize)
                .map((p) => (
                  <TRow key={p.url}>
                    <TD className="max-w-lg truncate">
                      <a className="underline" href={p.url} target="_blank" rel="noreferrer">
                        {p.url}
                      </a>
                    </TD>
                    <TD className="text-sm">{p.title || "—"}</TD>
                  </TRow>
                ))}
            </TBody>
          </Table>
        </div>
        <Pagination
          page={pagesPage}
          pageSize={pagesPageSize}
          total={
            (pagesQ
              ? pages.filter(
                  (p) =>
                    p.url.toLowerCase().includes(pagesQ.toLowerCase()) ||
                    (p.title || "").toLowerCase().includes(pagesQ.toLowerCase()),
                )
              : pages
            ).length
          }
          onChange={setPagesPage}
        />
      </Card>
      <Card className="p-4">
        <Toolbar searchValue={q} onSearchChange={setQ} placeholder="Rechercher par titre" />
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TRow>
                <TH>Titre</TH>
                <TH>Longueur</TH>
                <TH>Date</TH>
                <TH>Actions</TH>
              </TRow>
            </THead>
            <TBody>
              {slice.map((s) => (
                <TRow key={s.id}>
                  <TD className="max-w-sm">
                    <div className="font-medium">{s.title || "—"}</div>
                    <div className="text-xs text-muted">{s.lang || "—"}</div>
                  </TD>
                  <TD className="text-sm">{s.content_text.length}</TD>
                  <TD className="text-sm">{new Date(s.created_at).toLocaleString()}</TD>
                  <TD className="whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditing(s);
                        openEditModal();
                      }}
                    >
                      Éditer
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="ml-2"
                      onClick={() => void removeSample(s.id)}
                    >
                      Supprimer
                    </Button>
                  </TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
      </Card>
      <EditModal>
        {editing && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Éditer la référence</h3>
            <FormField label="Titre">
              <Input
                value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              />
            </FormField>
            <FormField label="Texte">
              <Textarea
                rows={8}
                value={editing.content_text}
                onChange={(e) => setEditing({ ...editing, content_text: e.target.value })}
              />
            </FormField>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(null);
                  closeEditModal();
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={async () => {
                  if (editing) {
                    await updateSample(editing);
                    setEditing(null);
                    closeEditModal();
                  }
                }}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        )}
      </EditModal>
      <Toasts />
    </PageLayout>
  );
}
